import { useEffect, useMemo, useState } from "react";
import { Loader2, Flame, Search, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { crmService } from "@/services/crmService";
import { LEAD_TYPES, PRIORITIES, TEMPERATURES, PREAPPROVAL_STATUSES } from "@/config/crmOptions";
import { Field } from "@/components/profile/FormControls";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyLead = {
  lead_type: "buyer", priority: "normal", temperature: "warm",
  price_min: "", price_max: "", desired_location: "", timeline: "",
  motivation: "", preapproval_status: "unknown", pipeline_stage_id: "",
};

const NONE = "__default__";

export const NewLeadDialog = ({ open, onOpenChange, presetContact, onCreated }) => {
  const [mode, setMode] = useState("existing"); // existing | new
  const [lead, setLead] = useState(emptyLead);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  // existing contact search
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  // inline new contact
  const [nc, setNc] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  const set = (k, v) => setLead((l) => ({ ...l, [k]: v }));
  const setNC = (k, v) => setNc((c) => ({ ...c, [k]: v }));

  useEffect(() => { if (open) crmService.listStages().then(setStages).catch(() => {}); }, [open]);

  useEffect(() => {
    if (open) {
      setLead(emptyLead); setErrors({}); setQ(""); setResults([]); setDuplicate(null); setNc({ first_name: "", last_name: "", email: "", phone: "" });
      if (presetContact) { setMode("existing"); setSelected(presetContact); }
      else { setMode("existing"); setSelected(null); }
    }
  }, [open, presetContact]);

  useEffect(() => {
    if (mode !== "existing" || selected || q.trim().length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { contacts } = await crmService.listContacts({ search: q.trim(), tab: "all" });
        if (active) setResults(contacts.slice(0, 6));
      } catch { /* ignore */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, mode, selected]);

  const canSave = useMemo(() => {
    if (mode === "existing") return !!selected;
    return nc.first_name.trim() && nc.last_name.trim();
  }, [mode, selected, nc]);

  const buildPayload = () => ({
    lead_type: lead.lead_type,
    priority: lead.priority,
    temperature: lead.temperature,
    price_min: lead.price_min === "" ? null : Number(lead.price_min),
    price_max: lead.price_max === "" ? null : Number(lead.price_max),
    desired_location: lead.desired_location || null,
    timeline: lead.timeline || null,
    motivation: lead.motivation || null,
    preapproval_status: lead.preapproval_status || null,
    pipeline_stage_id: lead.pipeline_stage_id || null,
  });

  const submit = async ({ force = false, useExistingId = null } = {}) => {
    setErrors({});
    setSaving(true);
    try {
      const payload = buildPayload();
      if (useExistingId) {
        payload.contact_id = useExistingId; // attach lead to the existing contact
      } else if (mode === "existing") {
        payload.contact_id = selected.id;
      } else {
        payload.new_contact = { ...nc, relationship_type: "lead" };
      }
      const data = await crmService.createLead(payload, force);
      if (data.created === false && data.duplicate) {
        setDuplicate(data.duplicate);
        return;
      }
      toast.success("Lead created");
      onCreated?.(data.lead);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  const save = () => submit();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flame className="h-5 w-5 text-accent" /> New Lead</DialogTitle>
          <DialogDescription>Attach this lead to an existing contact, or create the contact inline.</DialogDescription>
        </DialogHeader>

        {duplicate ? (
          <div className="space-y-4" data-testid="lead-duplicate-warning">
            <div className="flex items-start gap-3 rounded-lg border border-amber-400/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">Possible duplicate found</p>
                <p className="mt-1">A contact with the same email or phone already exists in your workspace:</p>
                <p className="mt-2 font-medium">
                  {duplicate.first_name} {duplicate.last_name}
                  {duplicate.email ? ` · ${duplicate.email}` : ""}{duplicate.phone ? ` · ${duplicate.phone}` : ""}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => submit({ useExistingId: duplicate.id })} disabled={saving} data-testid="lead-dup-use-existing">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Use Existing Contact"}
              </Button>
              <Button variant="secondary" onClick={() => setDuplicate(null)} disabled={saving} data-testid="lead-dup-back">Back to edit</Button>
              <Button onClick={() => submit({ force: true })} disabled={saving} data-testid="lead-dup-create-anyway">Create Anyway</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        {!presetContact && (
          <div className="inline-flex rounded-lg border border-border p-1" data-testid="lead-contact-mode">
            {[["existing", "Existing contact"], ["new", "New contact"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => { setMode(k); setSelected(null); }} data-testid={`lead-mode-${k}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === k ? "bg-brand-navy text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {mode === "existing" ? (
          <div className="space-y-2">
            {selected ? (
              <div className="flex items-center justify-between rounded-lg border border-brand-navy/30 bg-secondary/40 px-3 py-2" data-testid="lead-selected-contact">
                <span className="text-sm font-medium">{selected.first_name} {selected.last_name}{selected.email ? ` · ${selected.email}` : ""}</span>
                {!presetContact && <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="lead-clear-contact">Change</Button>}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 rounded-lg border border-border px-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts by name or email…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="lead-contact-search" />
                </div>
                {results.length > 0 && (
                  <div className="rounded-lg border border-border">
                    {results.map((c) => (
                      <button key={c.id} type="button" onClick={() => setSelected(c)} data-testid={`lead-result-${c.id}`}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary">
                        <span>{c.first_name} {c.last_name}{c.email ? ` · ${c.email}` : ""}</span>
                        <Check className="h-4 w-4 text-transparent" />
                      </button>
                    ))}
                  </div>
                )}
                {q.trim().length >= 2 && results.length === 0 && (
                  <p className="text-xs text-muted-foreground">No matching contacts. Switch to “New contact” to create one.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor="nl-first" error={errors.first_name}>
              <Input id="nl-first" value={nc.first_name} onChange={(e) => setNC("first_name", e.target.value)} data-testid="lead-new-first" />
            </Field>
            <Field label="Last name" htmlFor="nl-last" error={errors.last_name}>
              <Input id="nl-last" value={nc.last_name} onChange={(e) => setNC("last_name", e.target.value)} data-testid="lead-new-last" />
            </Field>
            <Field label="Email" htmlFor="nl-email" error={errors.email}>
              <Input id="nl-email" value={nc.email} onChange={(e) => setNC("email", e.target.value)} data-testid="lead-new-email" />
            </Field>
            <Field label="Phone" htmlFor="nl-phone" error={errors.phone}>
              <Input id="nl-phone" value={nc.phone} onChange={(e) => setNC("phone", e.target.value)} data-testid="lead-new-phone" />
            </Field>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Lead type" htmlFor="nl-type" error={errors.lead_type}>
            <Select value={lead.lead_type} onValueChange={(v) => set("lead_type", v)}>
              <SelectTrigger id="nl-type" data-testid="lead-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Priority" htmlFor="nl-priority">
            <Select value={lead.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger id="nl-priority" data-testid="lead-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Temperature" htmlFor="nl-temp">
            <Select value={lead.temperature} onValueChange={(v) => set("temperature", v)}>
              <SelectTrigger id="nl-temp" data-testid="lead-temperature"><SelectValue /></SelectTrigger>
              <SelectContent>{TEMPERATURES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Price min" htmlFor="nl-pmin" error={errors.price_min}>
            <Input id="nl-pmin" type="number" min="0" value={lead.price_min} onChange={(e) => set("price_min", e.target.value)} data-testid="lead-price-min" />
          </Field>
          <Field label="Price max" htmlFor="nl-pmax" error={errors.price_max}>
            <Input id="nl-pmax" type="number" min="0" value={lead.price_max} onChange={(e) => set("price_max", e.target.value)} data-testid="lead-price-max" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Desired location" htmlFor="nl-loc" error={errors.desired_location}>
            <Input id="nl-loc" value={lead.desired_location} onChange={(e) => set("desired_location", e.target.value)} data-testid="lead-location" placeholder="e.g. West Austin" />
          </Field>
          <Field label="Timeline" htmlFor="nl-timeline" error={errors.timeline}>
            <Input id="nl-timeline" value={lead.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="lead-timeline" placeholder="e.g. 3–6 months" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Pre-approval" htmlFor="nl-preapp">
            <Select value={lead.preapproval_status} onValueChange={(v) => set("preapproval_status", v)}>
              <SelectTrigger id="nl-preapp" data-testid="lead-preapproval"><SelectValue /></SelectTrigger>
              <SelectContent>{PREAPPROVAL_STATUSES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Starting stage" htmlFor="nl-stage">
            <Select value={lead.pipeline_stage_id || NONE} onValueChange={(v) => set("pipeline_stage_id", v === NONE ? "" : v)}>
              <SelectTrigger id="nl-stage" data-testid="lead-stage"><SelectValue placeholder="New Lead" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>New Lead (default)</SelectItem>
                {stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Motivation (optional)" htmlFor="nl-motivation" error={errors.motivation}>
          <Textarea id="nl-motivation" rows={2} value={lead.motivation} onChange={(e) => set("motivation", e.target.value)} data-testid="lead-motivation" />
        </Field>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-lead-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !canSave} data-testid="new-lead-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : "Create Lead"}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
